import { Copy, RotateCcw, Trash2, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { apiMessage } from '../api/errors.ts';
import { api } from '../api/instance.ts';
import type { Schemas } from '../api/types.ts';
import { timeAgo } from '../lib/format.ts';
import { services } from '../services/index.ts';
import { ask, toast } from '../state/feedback.ts';
import { Button } from '../ui/Button.tsx';
import { Field } from '../ui/Field.tsx';
import { IconButton } from '../ui/IconButton.tsx';
import { Section } from '../ui/Section.tsx';
import {
  assignableRoles,
  canChangeRole,
  canRemove,
  canTransferOwnership,
  invitableRoles,
  isAdmin,
  ROLE_LABELS,
  type Role,
} from './roles.ts';

type Member = Schemas['Membership'];
type Invite = Schemas['Invite'];

export function inviteLink(token: string): string {
  return `${location.origin}/invite?token=${encodeURIComponent(token)}`;
}

async function copyInvite(token: string) {
  const copied = await (navigator.clipboard?.writeText(inviteLink(token)) ?? Promise.reject()).then(
    () => true,
    () => false,
  );
  if (copied) toast('Invite link copied');
  else toast('Couldn’t copy the invite link. Use the copy button next to the invite.', 'error');
}

/** Members, roles and invites (docs/spec/08 §4). */
export function Members({ orgId, me, myRole, seats }: { orgId: string; me: string; myRole: Role; seats: number }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [renders, setRenders] = useState<Record<string, number>>({});
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [busy, setBusy] = useState<string | null>(null);
  const admin = isAdmin(myRole);

  const reload = useCallback(async () => {
    try {
      const [m, inv, usage] = await Promise.all([
        api.members(orgId),
        admin ? api.invites(orgId) : Promise.resolve({ invites: [] }),
        admin ? api.orgUsage(orgId).catch(() => null) : Promise.resolve(null),
      ]);
      setMembers(m.members);
      setInvites(inv.invites);
      setRenders(Object.fromEntries((usage?.monthToDate.members ?? []).map((u) => [u.userId, u.renders])));
    } catch (e) {
      toast(apiMessage(e, 'Could not load the members.'), 'error');
    }
  }, [orgId, admin]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const run = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try {
      await fn();
      if (ok) toast(ok, 'success');
      await reload();
      await services.account.refreshMe();
    } catch (e) {
      toast(apiMessage(e, 'That didn’t work.'), 'error');
    } finally {
      setBusy(null);
    }
  };

  const used = (members?.length ?? 0) + invites.length;

  return (
    <div className="flex flex-col gap-6">
      <Section title={`Members · ${members?.length ?? '…'} of ${seats} seats`}>
        <div className="flex flex-col divide-y divide-border rounded-md bg-surface">
          {(members ?? []).map((m) => {
            const self = m.userId === me;
            const r = m.role as Role;
            return (
              <div key={m.userId} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid="member-row">
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="t-label truncate">
                    {m.email ?? m.userId}
                    {self ? ' (you)' : ''}
                  </span>
                  <span className="t-caption text-dim">
                    {m.lastActiveAt
                      ? `Active ${timeAgo(Date.parse(m.lastActiveAt))}`
                      : `Joined ${timeAgo(Date.parse(m.createdAt))}`}
                    {(m.rendersMtd ?? renders[m.userId]) != null
                      ? ` · ${m.rendersMtd ?? renders[m.userId]} renders this month`
                      : ''}
                  </span>
                </div>
                {canChangeRole(myRole, r, self) ? (
                  <select
                    aria-label={`Role of ${m.email ?? m.userId}`}
                    className="t-label h-9 rounded-md border border-border bg-surface2 px-2"
                    value={r}
                    disabled={busy !== null}
                    onChange={(e) =>
                      void run(
                        `role-${m.userId}`,
                        () => api.updateMember(orgId, m.userId, e.target.value as Role),
                        'Role updated',
                      )
                    }
                  >
                    {[r, ...assignableRoles(myRole).filter((x) => x !== r)].map((x) => (
                      <option key={x} value={x}>
                        {ROLE_LABELS[x]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="t-caption rounded-full bg-surface3 px-2 py-0.5">{ROLE_LABELS[r]}</span>
                )}
                {canTransferOwnership(myRole) && !self ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy !== null}
                    onClick={async () => {
                      const ok = await ask({
                        title: `Make ${m.email ?? 'this member'} the owner?`,
                        message: 'They’ll manage billing and can remove anyone. You’ll become an admin.',
                        actions: [
                          { label: 'Transfer ownership', value: true, variant: 'danger' },
                          { label: 'Cancel', value: false, variant: 'ghost' },
                        ],
                      });
                      if (ok)
                        await run(
                          `owner-${m.userId}`,
                          () => api.transferOwnership(orgId, m.userId),
                          'Ownership transferred',
                        );
                    }}
                  >
                    Make owner
                  </Button>
                ) : null}
                {canRemove(myRole, r, self) ? (
                  <IconButton
                    icon={Trash2}
                    size={36}
                    label={self ? 'Leave company' : `Remove ${m.email ?? 'member'}`}
                    onClick={async () => {
                      const ok = await ask({
                        title: self ? 'Leave this company?' : `Remove ${m.email ?? 'this member'}?`,
                        message: self
                          ? 'You’ll lose access to its projects and price list.'
                          : 'They’ll lose access to the company’s projects and price list.',
                        actions: [
                          { label: self ? 'Leave' : 'Remove', value: true, variant: 'danger' },
                          { label: 'Cancel', value: false, variant: 'ghost' },
                        ],
                      });
                      if (ok)
                        await run(
                          `rm-${m.userId}`,
                          () => api.removeMember(orgId, m.userId),
                          self ? 'You left the company' : 'Member removed',
                        );
                    }}
                  />
                ) : null}
              </div>
            );
          })}
          {members === null ? <p className="t-body px-4 py-3 text-dim">Loading…</p> : null}
        </div>
      </Section>

      {admin ? (
        <Section title="Invite">
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void run(
                'invite',
                async () => {
                  const inv = await api.createInvite(orgId, email.trim(), role);
                  setEmail('');
                  if (inv.token) await copyInvite(inv.token);
                },
                'Invite sent',
              );
            }}
          >
            <Field
              label="Email"
              type="email"
              required
              className="min-w-[220px] flex-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <label className="flex flex-col gap-1">
              <span className="t-caption text-muted">Role</span>
              <select
                aria-label="Invite role"
                className="t-label h-10 rounded-sm border border-border bg-surface2 px-2"
                value={role}
                onChange={(e) => setRole(e.target.value as typeof role)}
              >
                {invitableRoles(myRole).map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABELS[r]}
                  </option>
                ))}
              </select>
            </label>
            <Button type="submit" icon={UserPlus} loading={busy === 'invite'} disabled={!email.trim() || busy !== null}>
              Send invite
            </Button>
          </form>
          {used >= seats ? (
            <p className="t-caption text-accent">
              All seats are in use. Add seats under Billing before inviting more people.
            </p>
          ) : null}
          {invites.length ? (
            <div className="flex flex-col divide-y divide-border rounded-md bg-surface">
              {invites.map((inv) => (
                <div key={inv.id} className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid="invite-row">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="t-label truncate">{inv.email}</span>
                    <span className="t-caption text-dim">
                      {ROLE_LABELS[inv.role as Role]} · expires {new Date(inv.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                  {inv.token ? (
                    <IconButton
                      icon={Copy}
                      size={36}
                      label="Copy invite link"
                      onClick={() => void copyInvite(inv.token!)}
                    />
                  ) : null}
                  <IconButton
                    icon={RotateCcw}
                    size={36}
                    label={`Resend invite to ${inv.email}`}
                    onClick={() =>
                      void run(
                        `resend-${inv.id}`,
                        async () => {
                          const fresh = await api.resendInvite(orgId, inv.id);
                          if (fresh.token) await copyInvite(fresh.token);
                        },
                        'Invite sent again',
                      )
                    }
                  />
                  <IconButton
                    icon={Trash2}
                    size={36}
                    label={`Revoke invite to ${inv.email}`}
                    onClick={() =>
                      void run(`revoke-${inv.id}`, () => api.revokeInvite(orgId, inv.id), 'Invite revoked')
                    }
                  />
                </div>
              ))}
            </div>
          ) : null}
        </Section>
      ) : null}
    </div>
  );
}
